import React, { FC, useState, useEffect, useMemo, useRef } from 'react';
import {
  Flex,
  Text,
  Center,
  Heading,
  CircularProgress,
  Stack,
  Button,
} from '@chakra-ui/react';
import { useMutation, useQuery } from 'react-query';
import { format, formatInTimeZone } from 'date-fns-tz';
import { exportItemCountCsv, fetchItemCount } from 'api/items';
import { useWashHubListener } from 'hooks';
import { useSKU } from 'hooks/useSKU';
import { resolveCategorySkuIds } from 'constants/inventoryCategories';
import {
  buildLocationCsv,
  buildLocationRows,
  isSummaryTab,
  sumColumnTotals,
  SummaryTab,
  uniqueSkus,
} from './itemsByLocation';
import { ItemsByLocationTable } from './ItemsByLocationTable';
import { washHub } from 'store/slices';
import { Filters } from 'components/primitives/Filters';
import { Status } from 'types/items';
import { ItemsViewProps } from './ItemsSummary.props';
import DatepickerFilter from 'components/modules/DatepickerFilter';
import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';
import { useTheme } from '@chakra-ui/system';
import { MdUploadFile } from 'react-icons/md';
import { CSVLink } from 'react-csv';
import { getTooltipLabel } from './utils';
import { InfoTooltip } from 'components/primitives/Tooltip';
import moment from 'moment';

const SUMMARY_TABS: { key: SummaryTab; label: string }[] = [
  { key: 'summary', label: 'Item Summary' },
  { key: 'locations', label: 'Items by Locations' },
];

const ItemsView: FC<ItemsViewProps> = (_) => {
  const queryUrl = useQueryParams();
  const csvRef = useRef<any>(null);

  const [sku, setSku] = useState<string[]>();
  const [category, setCategory] = useState<string[]>();
  // Seeded from the url in the initializer rather than an effect: useUrlSharing
  // rewrites the query string on the first render, so an effect would read back
  // a value that had already been overwritten.
  const [tab, setTab] = useState<SummaryTab>(() =>
    isSummaryTab(queryUrl.tab) ? queryUrl.tab : 'summary',
  );
  const [searchDate, setSearchDate] = useState<Date>(
    ((queryUrl.searchDate as unknown) as Date) ||
      ((format(new Date(), 'yyyy-MM-dd') as unknown) as Date),
  );
  const [status, setStatus] = useState<undefined | number[]>(undefined);
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [urlRecord, setUrlRecord] = useState({});

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);

  // The counts API filters by SKU id only, so the selected categories are
  // resolved against the SKU lists here.
  //
  // Both calls skip the wash hub filter. Passing a hub makes /sku/list narrow
  // item SKUs to the ones reachable through that hub's packages, so picking
  // the Item category returned the handful a hub's customers currently order
  // rather than every item SKU -- a filter narrower than the table it filters,
  // which hid rows the unfiltered view was showing (ClickUp 86bbmcv2b).
  const { data: itemSkus } = useSKU(
    'item',
    washHubId,
    undefined,
    undefined,
    true,
  );
  const { data: equipmentSkus } = useSKU(
    'equipment',
    washHubId,
    undefined,
    undefined,
    true,
  );

  const effectiveSkus = useMemo(
    () => resolveCategorySkuIds(sku, category, itemSkus, equipmentSkus),
    [sku, category, itemSkus, equipmentSkus],
  );

  const queryKey = [
    `fetch-item-count-${washHubId}`,
    status,
    effectiveSkus,
    searchDate,
  ];

  const queryFn = () =>
    fetchItemCount({
      washhubId: washHubId,
      sku: effectiveSkus,
      date: searchDate,
    });

  const options = {
    enabled: isWashHubIdValid, //!!washHubId
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const {
    mutate: mutateCsv,
    data: csvData = '',
    isLoading: isLoadingCSV,
  } = useMutation(
    async () => {
      const response = await exportItemCountCsv({
        washhubId: washHubId,
        sku: effectiveSkus,
        date: searchDate,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click();
        }, 500);
      },
    },
  );

  useWashHubListener(() => {
    setStatus(undefined);
    setSku(undefined);
    setCategory(undefined);
  });

  const additionalDataCounts = data?.additionalData?.counts || undefined;

  const getItemsByWashHub = () => {
    const items = data?.items;
    return items;
  };

  // The Items by Locations pivot: the same response reshaped, so switching
  // tabs costs no extra request (ClickUp 86bbmcuwa).
  const locationSkus = useMemo(() => uniqueSkus((data?.items || []) as any), [
    data,
  ]);

  const locationRows = useMemo(
    () =>
      buildLocationRows({
        skus: locationSkus,
        hub: {
          _id: String(washHubId || 'hub'),
          name: `${selectedWashHub?.name ?? ''} Wash Hub`.trim(),
          washHub: selectedWashHub?.name ?? '',
        },
        items: (data?.items || []) as any,
        byLocation: data?.additionalData?.byLocation || [],
        sites: data?.additionalData?.sites || [],
      }),
    [locationSkus, washHubId, selectedWashHub, data],
  );

  const locationTotals = useMemo(
    () => sumColumnTotals(locationRows, locationSkus),
    [locationRows, locationSkus],
  );

  const onApplyFilters = (data: string[], type: string) => {
    if (type === 'sku') {
      setSku(data.length ? data : undefined);
    }
    // Normalised to undefined when empty so an emptied filter drops out of the
    // query key and the shared URL rather than lingering as an empty list.
    if (type === 'category') {
      setCategory(data.length ? data : undefined);
    }
  };

  const onClearFilters = () => {
    setSku(undefined);
    setCategory(undefined);
  };

  const calSubtotalAtHubs = (
    counts: { [key: string]: number } | undefined,
  ): number => {
    if (!counts) return 0;
    const inProcess = calInProcess(counts);
    const packed = counts[Status.PACKED] || (0 as number);
    return inProcess + packed;
  };

  const calSubtotalInMarkets = (
    counts: { [key: string]: number } | undefined,
  ): number => {
    if (!counts) return 0;
    const inProcess = calInProcess(counts);
    const packed = counts[Status.PACKED] || (0 as number);
    const delivered = counts[Status.DELIVERED] || (0 as number);
    return inProcess + packed + delivered;
  };

  const calInProcess = (
    counts: { [key: string]: number } | undefined,
  ): number => {
    if (!counts) return 0;
    const statusesToSum = [
      Status.WASHED,
      Status.DRY,
      Status.UNUSED,
      Status.UNPACK,
      Status.OPEN,
      Status.RECONCILED_FOUND,
    ];
    return statusesToSum.reduce((sum, status) => {
      const value = counts![status];
      return sum + (value || 0);
    }, 0);
  };

  const onDateChange = (value: any) => {
    setSearchDate(value);
  };
  const theme = useTheme();

  const pc = theme?.colors?.primary['800'];

  const getFormatedDate = (date: Date): any => {
    return moment.utc(date).format('YYYY-MM-DD');
  };

  useEffect(() => {
    console.log({ status, sku, searchDate });
    return () => {};
  }, [status, sku, searchDate]);

  useEffect(() => {
    const urlParams = {
      washHubId: String(washHubId),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
      // Comma-separated rather than the '-' used above: category names are
      // words, not ids, and some of them contain no hyphen only by luck.
      ...(category && category.length ? { category: category.join(',') } : {}),
      ...(searchDate
        ? {
            searchDate: `${moment.utc(searchDate).format('YYYY-MM-DD')}`,
          }
        : {}),
      // Only the non-default tab is written, so an ordinary link stays clean.
      ...(tab !== 'summary' ? { tab } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, status, searchDate, sku, category, tab]);

  useEffect(() => {
    const { status, sku, category, searchDate } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    sku && setSku(sku.split('-'));
    category && setCategory(category.split(','));
    searchDate && setSearchDate(getFormatedDate(new Date(searchDate)));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  if (!isUrlMapped) return null;

  const renderExpBtn = () => {
    // The two tabs export different shapes, so the button follows the one on
    // screen. Item Summary asks the server for its csv; the pivot is built
    // from the rows already in hand, so it needs no endpoint and cannot drift
    // from the table it is exporting.
    if (tab === 'locations') {
      const href = `data:text/csv;charset=utf-8,${encodeURIComponent(
        buildLocationCsv(locationRows, locationSkus, locationTotals),
      )}`;

      return (
        <Button
          as="a"
          ml={2}
          minW="200px"
          href={href}
          download={`washhub-${selectedWashHub?.name}-items-by-location.csv`}
          isDisabled={locationSkus.length === 0}>
          Export Items to CSV
        </Button>
      );
    }

    return (
      <>
        <Button
          isLoading={isLoadingCSV}
          ml={2}
          minW="200px"
          onClick={() => {
            mutateCsv();
          }}>
          Export Items to CSV
        </Button>
        {!isLoadingCSV && Boolean(csvData) && (
          <a
            href={`data:text/csv;charset=utf-8,${csvData}`}
            download={`washhub-${selectedWashHub?.name}-inventory-summary.csv`}
            style={{ display: 'none' }}
            ref={csvRef}>
            Export CSV
          </a>
        )}
      </>
    );
  };
  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading fontSize="24px">Inventory Summary</Heading>
        <Stack isInline>{renderExpBtn()}</Stack>
      </Flex>
      <Flex h="20px" />
      <Flex direction="column">
        <Flex>
          <DatepickerFilter
            name="dateFilter"
            maxDate={new Date()}
            label="Search Date"
            searchDate={searchDate}
            onDateChange={onDateChange}
          />
        </Flex>
        <Flex>
          <Filters
            isFullButton
            venue={sku}
            categories={category}
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
            isSkuOnly={true}
            isCategoriesOnly={true}
          />
        </Flex>
        <Stack mb={4} isInline>
          {SUMMARY_TABS.map(({ key, label }) => (
            <Button
              key={key}
              w="190px"
              borderRadius="5px"
              variant={tab === key ? 'solid' : 'outline'}
              onClick={() => setTab(key)}>
              {label}
            </Button>
          ))}
        </Stack>
        {tab === 'locations' ? (
          // No fixed height here: DataTable caps its own body and owns both
          // scroll axes, so an outer height would clip it instead.
          <ItemsByLocationTable
            skus={locationSkus}
            rows={locationRows}
            totals={locationTotals}
            isLoading={isLoading || isFetching}
          />
        ) : (
          <Flex direction="column" h="450px" pos="relative" mt={0}>
            <Flex w="100%" overflow="auto">
              <Flex
                overflow="auto"
                minW="100%"
                direction="column"
                fontSize="13px">
                <Flex
                  position="sticky"
                  top="0"
                  w="100%"
                  backdropFilter="blur(15px)"
                  flex={1}
                  bg={`${pc}80`}
                  color="support.500"
                  userSelect="none"
                  fontWeight="bold"
                  borderRadius="5px">
                  <Text px={6} py={4} flex={0.2}>
                    SKU
                  </Text>
                  <Text px={6} py={4} flex={0.2}>
                    Wash Hub
                  </Text>
                  <Flex
                    position={'relative'}
                    direction={'column'}
                    alignItems={'center'}
                    flex={0.1}
                    px={6}
                    py={4}
                    textAlign={'center'}>
                    <Text>In Process</Text>
                    <InfoTooltip
                      label={getTooltipLabel('inProcess')}
                      rightOffset={['5px', '5px', '17px']}
                    />
                  </Flex>
                  <Flex
                    position={'relative'}
                    direction={'column'}
                    alignItems={'center'}
                    flex={0.1}
                    px={6}
                    py={4}
                    textAlign={'center'}>
                    <Text>Packed</Text>
                    <InfoTooltip
                      label={getTooltipLabel('packed')}
                      rightOffset={['5px', '5px', '25px']}
                    />
                  </Flex>
                  <Flex
                    direction={'column'}
                    alignItems={'center'}
                    bg="#c9dbf9"
                    flex={0.1}
                    px={6}
                    py={4}
                    textAlign={'center'}>
                    <Text>Subtotal At Hubs</Text>
                    <Text fontSize={'0.5rem'}>In-Process + Packed</Text>
                  </Flex>
                  <Flex
                    position={'relative'}
                    direction={'column'}
                    alignItems={'center'}
                    flex={0.1}
                    px={6}
                    py={4}
                    textAlign={'center'}>
                    <Text>Delivered</Text>
                    <InfoTooltip
                      label={getTooltipLabel('delivered')}
                      rightOffset={['5px', '5px', '18px']}
                    />
                  </Flex>
                  <Flex
                    direction={'column'}
                    align={'center'}
                    bg="#d9e9d3"
                    px={6}
                    py={4}
                    flex={0.1}
                    textAlign={'center'}>
                    <Text>Subtotal In Markets</Text>
                    <Text fontSize={'0.55rem'}>Hub + Delivered</Text>
                  </Flex>
                </Flex>
                {isLoading || isFetching ? (
                  <Center py={20} px={5}>
                    <CircularProgress isIndeterminate color="primary.400" />
                  </Center>
                ) : data?.items && data.items.length > 0 ? (
                  <Flex flexDirection="column" flex={1}>
                    {data?.items.map((item: any, index: number) => (
                      <Flex
                        key={`${JSON.stringify(item)}-${index}`}
                        flex={1}
                        cursor="pointer"
                        color="support.500"
                        borderRadius="5px"
                        bg={(index + 1) % 2 === 0 ? `${pc}30` : undefined}
                        _hover={{
                          bg: 'primary.800',
                          transition: '0.3s',
                        }}>
                        <Text px={6} py={4} flex={0.2}>
                          {item?.name || 0}
                        </Text>
                        <Text px={6} py={4} flex={0.2}>
                          {item?.washHub || ''}
                        </Text>
                        <Text px={6} py={4} flex={0.1} textAlign={'center'}>
                          {calInProcess(item.breakdown)?.toLocaleString() || 0}
                        </Text>
                        <Text px={6} py={4} flex={0.1} textAlign={'center'}>
                          {item?.breakdown?.[Status.PACKED]?.toLocaleString() ||
                            0}
                        </Text>
                        <Text
                          px={6}
                          py={4}
                          bg="#c9dbf940"
                          flex={0.1}
                          textAlign={'center'}>
                          {' '}
                          {calSubtotalAtHubs(item.breakdown)?.toLocaleString()}
                        </Text>
                        <Text px={6} py={4} flex={0.1} textAlign={'center'}>
                          {' '}
                          {item?.breakdown?.[
                            Status.DELIVERED
                          ]?.toLocaleString() || 0}
                        </Text>
                        <Text
                          bg="#d9e9d340"
                          px={6}
                          py={4}
                          flex={0.1}
                          textAlign={'center'}>
                          {calSubtotalInMarkets(
                            item.breakdown,
                          )?.toLocaleString()}
                        </Text>
                      </Flex>
                    ))}
                    {
                      <Flex
                        flex={1}
                        bg={`${pc}80`}
                        position="sticky"
                        bottom="0"
                        w="100%"
                        fontWeight="bold"
                        backdropFilter="blur(15px)">
                        <Text px={6} py={4} flex={0.2}>
                          Total Items
                        </Text>
                        <Text px={6} py={4} flex={0.2}></Text>
                        <Text px={6} py={4} flex={0.1} textAlign={'center'}>
                          {calInProcess(
                            additionalDataCounts,
                          )?.toLocaleString() || 0}
                        </Text>
                        <Text px={6} py={4} flex={0.1} textAlign={'center'}>
                          {additionalDataCounts?.[
                            Status.PACKED
                          ]?.toLocaleString()}
                        </Text>
                        <Text
                          bg="#c9dbf9"
                          px={6}
                          py={4}
                          flex={0.1}
                          textAlign={'center'}>
                          {calSubtotalAtHubs(
                            additionalDataCounts,
                          )?.toLocaleString()}
                        </Text>
                        <Text px={6} py={4} flex={0.1} textAlign={'center'}>
                          {additionalDataCounts?.[
                            Status.DELIVERED
                          ]?.toLocaleString()}
                        </Text>
                        <Text
                          bg="#d9e9d3"
                          px={6}
                          py={4}
                          flex={0.1}
                          textAlign={'center'}>
                          {calSubtotalInMarkets(
                            additionalDataCounts,
                          )?.toLocaleString()}
                        </Text>
                      </Flex>
                    }
                  </Flex>
                ) : (
                  <Center p={8}>
                    <Text fontSize="20px">No Items available</Text>
                  </Center>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ItemsView;
